import type { DataTableColumn } from "@playbookai/ui";
import type { DateTimeFormatter } from "../../../settings/format-date-time.js";
import type { AuditEvent } from "../../../api/admin-audit-api.js";
import { actionLabel } from "../audit-format.js";
import {
  ActionCell,
  ActorCell,
  DetailsButton,
  EntityCell,
  TimeCell,
  timeHeader,
} from "./event-cells.js";

/* Four columns in the artboard's order — Time · Actor · Action · Affected —
   and a trailing chevron (PB-5216, d5). Widths are set on the predictable
   columns (time, actor) so the free-text ones absorb the remaining space
   instead of every column negotiating on content and shifting between pages.
   The Detail column is gone: its entries live in the drawer, where they have
   room, instead of truncated to one line beside four other cells. Built per
   formatter rather than as constants, because the time header and cells
   render in the reader's zone. */
export function auditColumns(
  formatter: DateTimeFormatter,
  open: (row: AuditEvent) => void,
): ReadonlyArray<DataTableColumn<AuditEvent>> {
  return [
    {
      id: "ts",
      /* Column floors (PB-5436): each is this column's width at a 1280px window
         with the rail closed — the narrowest width this table is clean at today —
         so a narrower box scrolls inside the pinned region instead of clipping. */
      minWidth: 150,
      header: timeHeader(formatter),
      width: "16%",
      cardRole: "meta",
      cell: (row) => <TimeCell iso={row.ts} formatter={formatter} />,
    },
    {
      id: "actor",
      minWidth: 207,
      header: "Actor",
      width: "22%",
      cardRole: "meta",
      cell: (row) => (
        <ActorCell
          actorId={row.actorId}
          actorName={row.actorName}
          actorEmail={row.actorEmail}
        />
      ),
    },
    /* Source folded into the action cell rather than owning a column: six
       columns forced every one to a single word per line and clipped the last. */
    {
      id: "action",
      minWidth: 338,
      header: "Action",
      /* Declared, because this cell holds a label AND a service pill. Under
         auto layout the widest row on the CURRENT page sized the column, so
         every page, filter and refetch re-laid the table out. */
      width: "36%",
      cardRole: "title",
      cell: (row) => <ActionCell action={row.action} service={row.service} />,
    },
    {
      id: "entity",
      minWidth: 207,
      header: "Affected",
      width: "22%",
      cardRole: "meta",
      cell: (row) => (
        <EntityCell
          entityType={row.entityType}
          entityId={row.entityId}
          entityName={row.entityName}
        />
      ),
    },
    {
      id: "open",
      minWidth: 48,
      header: <span className="sr-only">Details</span>,
      /* A fixed width, not a share (PB-5436): the 24px button plus the cell's
         24px of padding. At 4% the column fell below 48px whenever the table
         was narrower than 1200px and clipped the button. */
      width: "3rem",
      align: "end",
      cardRole: "actions",
      cell: (row) => (
        <DetailsButton
          label={`Event details: ${actionLabel(row.action)}`}
          onOpen={() => open(row)}
        />
      ),
    },
  ];
}
